import { DivCheckbox, Form } from "@talladega/components";
import {
  AimIcon,
  ChevronUpIcon,
  CloseIcon,
  SearchIcon,
} from "@talladega/icons";
import {
  findClosestLocationIndex,
  getDistance,
  isAutoCompleteProvince,
  isLocationWithinDefaultMaxRange,
  isValidAutoCompleteAddress,
  getDistanceEvaluationGeneric,
} from "@common/helpers/distanceMatrixHelper";
import { Disclosure } from "@headlessui/react";
import GoogleMapsWrapper from "@modules/location/helpers/GoogleMapsWrapper";
import reverseGeocode from "@modules/location/reverseGeoCode";
import useSaveLocationMutation from "@modules/user/hooks/useSaveLocationMutation";
import classNames from "classnames";
import { useTranslation } from "next-i18next";
import { useCallback, useState } from "react";
import {
  FilterAction,
  FilterActions,
  FilterKeys,
  LocationFilter,
  ProvinceFilter,
  VehicleFilter,
  VehicleFilterLocationModel,
  VehicleFilterProvinceLocation,
} from "../types/vehicleFilter";
import FilterDisclosure from "./FilterDisclosure";

type AutocompleteLocation = {
  isShown: boolean;
  distance?: number | null;
} & VehicleFilterLocationModel;

type Props = {
  provincesAndLocations: VehicleFilterProvinceLocation[];
  vehicleFilters: VehicleFilter[];
  onUpdate(filterAction: FilterAction): void;
};

type LocationDisplay = {
  id: string;
  name: string;
  displayName: string;
  isSelected: boolean;
};

type ProvinceLocationDisplay = {
  id: number;
  name: string;
  selectedFilterCount: number;
  isSelected: boolean;
  locations: LocationDisplay[];
};

const toProvinceLocationsDisplay = (
  provincesAndLocations: VehicleFilterProvinceLocation[],
  provinceFilters: ProvinceFilter[],
  locationFilters: LocationFilter[]
) => {
  const provinceLocationDisplays: ProvinceLocationDisplay[] = [];

  provincesAndLocations.forEach((province) => {
    const matchingProvinceFilters = provinceFilters.filter(
      (f) => f.state.id === province.id.toString()
    );
    const matchingLocationFilters = locationFilters.filter(
      (f) => f.state.provinceId === province.id
    );

    provinceLocationDisplays.push({
      id: province.id,
      name: province.name,
      selectedFilterCount:
        matchingProvinceFilters.length + matchingLocationFilters.length,
      isSelected: matchingProvinceFilters.length > 0,
      locations: province.locations.map((location) => {
        // Determine if location is selected (filter exisits)
        const matchingToModel = matchingLocationFilters.find(
          (f) => f.state.id === location.id
        );

        return {
          id: location.id,
          name: location.name,
          displayName: location.displayName,
          isSelected: matchingToModel !== undefined,
        };
      }),
    });
  });

  return provinceLocationDisplays;
};

const filterForSearch = (
  provinceLocationDisplays: ProvinceLocationDisplay[],
  autocompleteLocations: AutocompleteLocation[]
) => {
  const provincesLocationsFiltered: ProvinceLocationDisplay[] = [];

  provinceLocationDisplays.forEach((province) => {
    // Always include selected provinces
    if (province.isSelected) {
      provincesLocationsFiltered.push(province);
      return;
    }

    const locations = province.locations.filter(
      (location) =>
        autocompleteLocations.some(
          (dl) => dl.isShown && dl.id === location.id
        ) || location.isSelected
    );

    // Include province and matched locations only
    if (locations.length > 0) {
      provincesLocationsFiltered.push({
        id: province.id,
        name: province.name,
        locations,
        selectedFilterCount: province.selectedFilterCount,
        isSelected: province.isSelected,
      });
    }
  });

  return provincesLocationsFiltered;
};

const VehicleLocationFilter = ({
  provincesAndLocations,
  vehicleFilters,
  onUpdate,
}: Props) => {
  const { t } = useTranslation(["vehiclesFilters", "common"]);
  const [search, setSearch] = useState("");
  const [locationDisabledError, setLocationDisabledError] = useState(false);
  const saveLocationMutation = useSaveLocationMutation();
  const [autocomplete, setAutocomplete] =
    useState<google.maps.places.Autocomplete | null>(null);

  const allAutocompleteLocations: AutocompleteLocation[] =
    provincesAndLocations.flatMap((p) => {
      return p.locations.map((l) => ({ ...l, isShown: true, distance: null }));
    });
  const [autocompleteLocations, setAutocompleteLocations] = useState<
    AutocompleteLocation[]
  >(allAutocompleteLocations);

  const provinceFilters = vehicleFilters.filter(
    (vf): vf is ProvinceFilter => vf.key === FilterKeys.Province
  );

  const locationFilters = vehicleFilters.filter(
    (vf): vf is LocationFilter => vf.key === FilterKeys.Location
  );

  const updateProvinceFilters = (
    event: React.ChangeEvent<HTMLInputElement>,
    province: ProvinceLocationDisplay
  ) => {
    if (event.target.checked) {
      onUpdate({
        type: FilterActions.AddProvinceFilter,
        payload: {
          id: province.id.toString(),
          name: province.name,
        },
      });
    } else {
      onUpdate({
        type: FilterActions.RemoveByIdAndType,
        payload: {
          type: FilterKeys.Province,
          id: province.id.toString(),
        },
      });
    }
  };

  const updateLocationFilters = (
    event: React.ChangeEvent<HTMLInputElement>,
    location: LocationDisplay,
    province: ProvinceLocationDisplay
  ) => {
    if (event.target.checked) {
      onUpdate({
        type: FilterActions.AddLocationFilter,
        payload: {
          id: location.id,
          name: location.name,
          displayName: location.displayName,
          provinceId: province.id,
        },
      });
    } else {
      onUpdate({
        type: FilterActions.RemoveByIdAndType,
        payload: {
          type: FilterKeys.Location,
          id: location.id,
        },
      });
    }
  };

  const updateAutocompleteLocations = useCallback(
    (rows: google.maps.DistanceMatrixResponseElement[] | null = null) => {
      if (rows && rows.length === allAutocompleteLocations.length) {
        let hasOne = false;
        const updateLocations = allAutocompleteLocations.map(
          (location, index) => {
            const isShown = isLocationWithinDefaultMaxRange(rows[index]);
            hasOne ||= isShown;
            return {
              ...location,
              isShown,
              distance: getDistance(rows[index]),
            };
          }
        );
        if (!hasOne) {
          // if no locations are within the max distance
          const location = updateLocations[findClosestLocationIndex(rows)];
          if (location) location.isShown = true;
        }
        setAutocompleteLocations(updateLocations);
      } else {
        setAutocompleteLocations(
          allAutocompleteLocations.map((location) => ({
            ...location,
            isShown: true,
            distance: null,
          }))
        );
      }
    },
    [allAutocompleteLocations]
  );

  const updateAutocompleteLocationsByProvince = useCallback(
    (province: google.maps.GeocoderAddressComponent) => {
      const sortedLocations = allAutocompleteLocations.map((location) => ({
        ...location,
        isShown: location.province === province.short_name,
        distance: null,
      }));

      setAutocompleteLocations(sortedLocations);
    },
    [allAutocompleteLocations, setAutocompleteLocations]
  );

  const handleAutoComplete = useCallback(
    (
      acLocations: VehicleFilterLocationModel[],
      position: { latitude: number; longitude: number }
    ) => {
      if (!position) return;

      const { latitude: lat, longitude: lng } = position;
      const distances = getDistanceEvaluationGeneric(
        { lat, lng },
        acLocations.map((l) => ({
          latitude: l.latitude || 0,
          longitude: l.longitude || 0,
        }))
      );

      updateAutocompleteLocations(distances);
    },
    [updateAutocompleteLocations]
  );

  const autoDetectClick = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLocationDisabledError(false);

          reverseGeocode(
            position.coords.latitude,
            position.coords.longitude
          ).then((result) => {
            if (result != null) {
              setSearch(
                `${result.city}, ${result.province}, ${result.country}`
              );
              handleAutoComplete(allAutocompleteLocations, position.coords);

              saveLocationMutation.mutateAsync({
                latitude: position.coords.latitude,
                longitude: position.coords.longitude,
                province: result.provinceAbbr,
                city: result.city,
              });
            }
          });
        },
        () => {
          setLocationDisabledError(true);
        },
        { enableHighAccuracy: true, maximumAge: 60000, timeout: 27000 }
      );
    }
  };

  const inputCallbackRef = useCallback(
    (inputEl: HTMLInputElement) => {
      if (autocomplete != null) return;
      if (inputEl == null) return;

      const ac = new google.maps.places.Autocomplete(inputEl, {
        fields: ["address_components", "geometry", "formatted_address"],
        componentRestrictions: { country: "ca" },
        strictBounds: false,
        types: ["(regions)"],
      });

      ac.addListener("place_changed", () => {
        setSearch(inputEl.value);

        const place = ac.getPlace();

        if (place.address_components && isAutoCompleteProvince(place)) {
          updateAutocompleteLocationsByProvince(place.address_components[0]);
        } else if (
          place.geometry?.location &&
          isValidAutoCompleteAddress(place)
        ) {
          handleAutoComplete(allAutocompleteLocations, {
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
          });
        }
      });
      setAutocomplete(ac);
    },
    [
      allAutocompleteLocations,
      autocomplete,
      handleAutoComplete,
      updateAutocompleteLocationsByProvince,
    ]
  );

  const clearLocationInput = () => {
    setSearch("");
    updateAutocompleteLocations();
  };

  const filterDisclosureClose = () => {
    setAutocomplete(null);
  };

  const provinceLocationDisplays = filterForSearch(
    toProvinceLocationsDisplay(
      provincesAndLocations,
      provinceFilters,
      locationFilters
    ),
    autocompleteLocations
  );

  const isFiltered = !autocompleteLocations.every((sl) => sl.isShown);

  return (
    <FilterDisclosure
      active={provinceFilters.length > 0 || locationFilters.length > 0}
      headerText={t("common:locations")}
      childPadding="sm"
      onClose={filterDisclosureClose}
    >
      <div className="space-y-2">
        <GoogleMapsWrapper>
          <Form.Input
            id="nearestLocation"
            placeholder={t("vehiclesFilters:find_your_nearest")}
            placeholderSize="small"
            border="thick"
            leftIcon={<SearchIcon className="text-primary-bold text-2xl" />}
            rightIcon={
              search ? (
                <CloseIcon
                  className="text-slate-500 text-md cursor-pointer"
                  onClick={() => clearLocationInput()}
                />
              ) : (
                <AimIcon
                  className="text-primary-bold text-xl cursor-pointer"
                  onClick={() => autoDetectClick()}
                />
              )
            }
            rightIconClickable
            ref={inputCallbackRef}
            onChange={(e) => {
              setSearch(e.currentTarget.value);
              if (!e.target.value) {
                clearLocationInput();
              }
            }}
            value={search}
          />

          {locationDisabledError && (
            <div className="text-error flex items-center border-error focus:border-error focus:shadow-error/8">
              <div className="caption-1">
                {t("common:location_disabled_error")}
              </div>
            </div>
          )}
        </GoogleMapsWrapper>

        <div className="space-y-2" key={search + isFiltered}>
          {provinceLocationDisplays.map((province) => (
            <div
              key={province.id}
              className="rounded-lg border border-gray-50 shadow-filter"
            >
              <Disclosure defaultOpen={isFiltered}>
                {({ open }) => (
                  <>
                    <Disclosure.Button className="flex w-full relative justify-between rounded-lg bg-white text-left pr-2 focus:outline-none focus-visible:shadow-elevation-03 py-2.5">
                      <div className="w-full focus:outline-none">
                        {province.selectedFilterCount > 0 && (
                          <span className="h-2 w-2 ml-2 rounded-full inline-block bg-secondary-salmon" />
                        )}
                        <h5
                          className={classNames(
                            "inline-block body-3 text-primary-deep",
                            {
                              "ml-4": province.selectedFilterCount === 0,
                              "ml-1.5 font-semibold":
                                province.selectedFilterCount > 0,
                            }
                          )}
                        >
                          {province.name}
                        </h5>
                      </div>

                      {province.selectedFilterCount > 0 && (
                        <div className="w-6 h-6 mr-2 flex flex-shrink-0 justify-center items-center bg-primary-light rounded-full">
                          <p className="caption-1 leading-6 text-primary-bold">
                            {province.selectedFilterCount}
                          </p>
                        </div>
                      )}
                      <ChevronUpIcon
                        className={`${
                          open
                            ? "text-primary-bold"
                            : "rotate-180 transform text-text-light-200"
                        } h-6 w-6 `}
                      />
                    </Disclosure.Button>
                    <Disclosure.Panel className="pt-0.5 pb-2.5 px-4 body-3 focus:outline-none">
                      <DivCheckbox
                        id={`shopAll${province.name}Filter`}
                        className="flex-col mb-2"
                        onChangeEvent={(event) =>
                          updateProvinceFilters(event, province)
                        }
                        objectValue={{
                          id: 0,
                          name: {
                            english: province.name,
                            french: province.name,
                          },
                          order: 0,
                        }}
                        isChecked={province.isSelected}
                      >
                        <span>{t("vehiclesFilters:shop_all")}</span>
                      </DivCheckbox>
                      <ul className="body-3 grid grid-cols-2 gap-2">
                        {province.locations.map((location, index) => (
                          <li key={location.id} className="grid">
                            <DivCheckbox
                              id={`shopAll${location.name}Filter`}
                              objectValue={{
                                id: index,
                                name: {
                                  english: location.displayName,
                                  french: location.displayName,
                                },
                                order: index,
                              }}
                              onChangeEvent={(event) =>
                                updateLocationFilters(event, location, province)
                              }
                              isChecked={location.isSelected}
                            >
                              <span>{location.displayName}</span>
                            </DivCheckbox>
                          </li>
                        ))}
                      </ul>
                    </Disclosure.Panel>
                  </>
                )}
              </Disclosure>
            </div>
          ))}
        </div>
      </div>
    </FilterDisclosure>
  );
};

export default VehicleLocationFilter;
